import React, { useState, useEffect } from "react";
import { useLocalization } from "../../localization/LocalizationContext.js";
import Button from "react-bootstrap/Button";
import allApi from "../../../../api/allApi";
import Modal from "react-bootstrap/Modal";
import errorIcon from "../../../../assets/images/error-2.png";
import { clampBeacon } from "../../common/configValidation";

function RadioConfig({networkId}) {
  const { localizationStrings } = useLocalization();
  const [deviceListing, setDeviceListing] = useState([]);
  const [serialNumber, setSerialNumber] = useState("");
  const [enableRadio24, setEnableRadio24] = useState(true);
  const [enableRadio5, setEnableRadio5] = useState(true);
  const [radioMode, setRadioMode] = useState("11be");
  const [radioMode5G, setRadioMode5G] = useState("11be");
  const [channels24, setChannels24] = useState([]);
  const [channels5, setChannels5] = useState([]);
  const [channelWidth24, setChannelWidth24] = useState("20MHz");
  const [channelWidth5, setChannelWidth5] = useState("20MHz");
  const [outputPower24, setOutputPower24] = useState();
  const [outputPower5, setOutputPower5] = useState();
  const [beaconInterval24, setBeaconInterval24] = useState(100);
  const [beaconInterval5, setBeaconInterval5] = useState(100);

  // API-driven dropdown states
  const [radioModes, setRadioModes] = useState([]);
  const [radioModes5GLow, setRadioModes5GLow] = useState([]);
  const [channel24, setChannel24] = useState("");
  const [channel5, setChannel5] = useState("");
  const [channelWidths, setChannelWidths] = useState([]);
  const [outputPowers, setOutputPowers] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  useEffect(() => {
    getDeviceDetails();
    fetchRadioModes();
    fetchRadioModes5Ghz();
    fetchChannels();
    fetchChannelWidths();
    fetchOutputPowers();
  }, [networkId]);

  const getDeviceDetails = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/deviceList?networkId=${networkId}`
      );
      if (response && response.response) {
        setDeviceListing(response.response);
      }
    } catch (error) {
      console.error("Error fetching devices", error);
    }
  };

  // Fetch available radio modes
  const fetchRadioModes = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest("api/http/radioModes24G");
      if (response && Array.isArray(response)) {
        setRadioModes(response);
        setRadioMode(response[0] || "");
      }
    } catch (error) {
      console.error("Error fetching radio modes", error);
    }
  };

  const fetchRadioModes5Ghz = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest("api/http/radioModes5GLow");
      if (response && Array.isArray(response)) {
        setRadioModes5GLow(response);
        setRadioMode5G(response[0] || "");
      }
    } catch (error) {
      console.error("Error fetching radio modes", error);
    }
  };

  // Fetch available channels
  const fetchChannels = async () => {
    try {
      const api = new allApi();
      const res = await api.getRequest("api/http/channel");
      if (Array.isArray(res) && res.length > 0) {
        const channels24Only = res.filter((ch) => ch.startsWith("2."));
        const channels5Only = res.filter((ch) => ch.startsWith("5."));
        setChannels24(channels24Only);
        setChannels5(channels5Only);
      }
    } catch (err) {
      console.error("Error fetching channels", err);
    }
  };

  // Fetch available channel widths
  const fetchChannelWidths = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest("api/http/channelWidth");
      if (response && Array.isArray(response)) {
        setChannelWidths(response);
        setChannelWidth24(response[0] || "");
        setChannelWidth5(response[0] || "");
      }
    } catch (error) {
      console.error("Error fetching channel widths", error);
    }
  };

  // Fetch available output powers
  const fetchOutputPowers = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest("api/http/outputPower");
      if (response && Array.isArray(response)) {
        setOutputPowers(response);
        setOutputPower24(response[0] || "");
        setOutputPower5(response[0] || "");
      }
    } catch (error) {
      console.error("Error fetching output powers", error);
    }
  };

  const handleInputChange = (e) => {
    const selectedSerial = e.target.value;
    setSerialNumber(selectedSerial);
    if (selectedSerial) {
      fetchRadioSettings(selectedSerial);
    }
  };

  const fetchRadioSettings = async (serialNo) => {
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/http/radioSettings?serialNo=${serialNo}`
      );
      if (response && response.status === "success" && response.data) {
        const { wlan0, wlan1 } = response.data;
        setEnableRadio24(wlan0.radioStatus === "1");
        setRadioMode(wlan0.operateMode || "");
        setChannelWidth24(wlan0.channelWidth || "");
        setOutputPower24(wlan0.txPower || "");
        setBeaconInterval24(clampBeacon(wlan0.beaconInterval));
        setChannel24(wlan0.channel || "");

        setEnableRadio5(wlan1.radioStatus === "1");
        setRadioMode5G(wlan1.operateMode || "");
        setChannelWidth5(wlan1.channelWidth || "");
        setOutputPower5(wlan1.txPower || "");
        setBeaconInterval5(clampBeacon(wlan1.beaconInterval));
        setChannel5(wlan1.channel || "");
      }
    } catch (error) {
      console.error("Error fetching radio settings", error);
    }
  };

  const handleSave = async () => {
    if (!serialNumber) {
      setModalMessage("Select a serial number before saving radio settings.");
      setShowError(true);
      return;
    }
    try {
      const api = new allApi();
      const beacon24 = clampBeacon(beaconInterval24);
      const beacon5 = clampBeacon(beaconInterval5);
      setBeaconInterval24(beacon24);
      setBeaconInterval5(beacon5);

      const payload = {
        wlan0: {
          channelWidth: channelWidth24,
          txPower: outputPower24,
          radioStatus: enableRadio24 ? "1" : "0",
          operateMode: radioMode,
          beaconInterval: String(beacon24),
          channel: channel24,
        },
        wlan1: {
          channelWidth: channelWidth5,
          txPower: outputPower5,
          radioStatus: enableRadio5 ? "1" : "0",
          operateMode: radioMode5G,
          beaconInterval: String(beacon5),
          channel: channel5,
        },
      };

      const response = await api.postRequest(
        `api/wireless/radioCofigSettings?serialNo=${serialNumber}`,
        "",
        payload
      );

      if (response?.status) {
        setModalMessage(response?.message || "Settings saved successfully!");
        setShowSuccess(true);
      } else {
        setModalMessage(
          response?.message || "Radio setiings Not Found."
        );
        setShowError(true);
      }
    } catch (error) {
      setModalMessage("Radio setiings Not Found.");
      setShowError(true);
    }
  };

  const handleCloseSuccess = () => setShowSuccess(false);
  const handleCloseError = () => setShowError(false);

  return (
    <div>
      <div className="tab-form w-scroll">
        <div className="col-lg-6">
          <label htmlFor="modelName" className="col-form-label">
            {localizationStrings?.serialNumber}
          </label>
          <select
            className="form-select"
            name="modelName"
            value={serialNumber}
            onChange={handleInputChange}
          >
            <option value="">{localizationStrings?.selectSerialNumber}</option>
            {deviceListing?.map((device, index) => (
              <option key={index} value={device.serialNumber}>
                {device.serialNumber}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="accordion mt-4" id="accordionRadio24">
        <div className="accordion-item">
          <h2 className="accordion-header" id="headingOne">
            <button
              className="accordion-button"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseOne"
              aria-expanded="true"
              aria-controls="collapseOne"
            >
              2.4GHz
            </button>
          </h2>
          <div
            id="collapseOne"
            className="accordion-collapse collapse show"
            aria-labelledby="headingOne"
            data-bs-parent="#accordionRadio24"
          >
            <div
              className="accordion-body"
              style={{
                maxHeight: "350px",
                overflowY: "auto",
                paddingRight: "8px",
              }}
            >
              <div className="row mb-4 mt-3">
                <div className="col-lg-6 d-flex align-items-center">
                  <strong style={{ marginRight: "16px" }}>
                    {localizationStrings?.enableRadio}
                  </strong>
                  <label className="ice-switch" style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={enableRadio24}
                      onChange={() => setEnableRadio24(!enableRadio24)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-lg-6">
                  <label className="col-form-label">
                    Radio Mode
                    <select
                      className="form-select"
                      style={{ maxWidth: "250px" }}
                      value={radioMode}
                      onChange={(e) => setRadioMode(e.target.value)}
                    >
                      {radioModes.map((mode, i) => (
                        <option key={i} value={mode}>
                          {mode}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {/* Channel Dropdown */}
                <div className="col-lg-6">
                  <label className="col-form-label">
                    Channel
                    <select
                      className="form-select"
                      style={{ maxWidth: "250px" }}
                      value={channel24}
                      onChange={(e) => setChannel24(e.target.value)}
                    >
                      {channels24.map((ch, i) => (
                        <option key={i} value={ch}>
                          {ch}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-lg-6">
                  <label className="col-form-label">
                    Channel Width
                    <select
                      className="form-select"
                      style={{ maxWidth: "250px" }}
                      value={channelWidth24}
                      onChange={(e) => setChannelWidth24(e.target.value)}
                    >
                      {channelWidths.map((cw, i) => (
                        <option key={i} value={cw}>
                          {cw}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="col-lg-6">
                  <label className="col-form-label">
                    Output Power
                    <select
                      className="form-select"
                      style={{ maxWidth: "250px" }}
                      value={outputPower24}
                      onChange={(e) => setOutputPower24(e.target.value)}
                    >
                      {outputPowers.map((pw, i) => (
                        <option key={i} value={pw}>
                          {pw}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <h4>{localizationStrings?.beaconInterval}</h4>
              <span style={{ fontSize: "12px", color: "#666" }}>
                100 to 300
              </span>
              <div className="row d-flex">
                <div className="col-lg-6">
                  <input
                    type="number"
                    className="form-control"
                    style={{ maxWidth: "250px" }}
                    min={100}
                    max={300}
                    placeholder={localizationStrings?.beaconInterval}
                    value={beaconInterval24}
                    onChange={(e) =>
                      setBeaconInterval24(Number(e.target.value))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="accordion mt-4" id="accordionRadio5">
        <div className="accordion-item">
          <h2 className="accordion-header" id="headingTwo">
            <button
              className="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseTwo"
              aria-expanded="false"
              aria-controls="collapseTwo"
            >
              5GHz
            </button>
          </h2>
          <div
            id="collapseTwo"
            className="accordion-collapse collapse"
            aria-labelledby="headingTwo"
            data-bs-parent="#accordionRadio5"
          >
            <div
              className="accordion-body"
              style={{
                maxHeight: "350px",
                overflowY: "auto",
                paddingRight: "8px",
              }}
            >
              <div className="row mb-4 mt-3">
                <div className="col-lg-6 d-flex align-items-center">
                  <strong style={{ marginRight: "16px" }}>
                    {localizationStrings?.enableRadio}
                  </strong>
                  <label className="ice-switch" style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={enableRadio5}
                      onChange={() => setEnableRadio5(!enableRadio5)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-lg-6">
                  <label className="col-form-label">
                    Radio Mode
                    <select
                      className="form-select"
                      style={{ maxWidth: "250px" }}
                      value={radioMode5G}
                      onChange={(e) => setRadioMode5G(e.target.value)}
                    >
                      {radioModes5GLow.map((mode, idx) => (
                        <option key={idx} value={mode}>
                          {mode}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="col-lg-6">
                  <label className="col-form-label">
                    Channel
                    <select
                      className="form-select"
                      style={{ maxWidth: "250px" }}
                      value={channel5}
                      onChange={(e) => setChannel5(e.target.value)}
                    >
                      {channels5.map((ch, i) => (
                        <option key={i} value={ch}>
                          {ch}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-lg-6">
                  <label className="col-form-label">
                    Channel Width
                    <select
                      className="form-select"
                      style={{ maxWidth: "250px" }}
                      value={channelWidth5}
                      onChange={(e) => setChannelWidth5(e.target.value)}
                    >
                      {channelWidths.map((cw, i) => (
                        <option key={i} value={cw}>
                          {cw}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="col-lg-6">
                  <label className="col-form-label">
                    Output Power
                    <select
                      className="form-select"
                      style={{ maxWidth: "250px" }}
                      value={outputPower5}
                      onChange={(e) => setOutputPower5(e.target.value)}
                    >
                      {outputPowers.map((pw, i) => (
                        <option key={i} value={pw}>
                          {pw}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <h4>{localizationStrings?.beaconInterval}</h4>
              <span style={{ fontSize: "12px", color: "#666" }}>
                100 to 300
              </span>
              <div className="row d-flex">
                <div className="col-lg-6">
                  <input
                    type="number"
                    className="form-control"
                    style={{ maxWidth: "250px" }}
                    min={100}
                    max={300}
                    placeholder={localizationStrings?.beaconInterval}
                    value={beaconInterval5}
                    onChange={(e) => setBeaconInterval5(Number(e.target.value))}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="wifi-footer">
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => serialNumber && fetchRadioSettings(serialNumber)}
        >
          {localizationStrings?.cancel}
        </button>
        <button
          type="button"
          className="text-btn primary-btn"
          onClick={handleSave}
          disabled={!serialNumber}
        >
          {localizationStrings?.save}
        </button>
      </div>

      <Modal
        show={showSuccess}
        onHide={handleCloseSuccess}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="inc-alert index-2000"
      >
        <span className="success-icon">
          <i className="icon done"></i>
        </span>
        <h3>{localizationStrings?.success}</h3>
        <p>{modalMessage}</p>
        <Button onClick={handleCloseSuccess}>{localizationStrings?.ok}</Button>
      </Modal>

      <Modal
        show={showError}
        onHide={handleCloseError}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="inc-alert index-2000"
      >
        <span className="error-icon">
          <img src={errorIcon} alt="Error" />
        </span>
        <h3>{localizationStrings?.error || "Error"}</h3>
        <p>{modalMessage}</p>
        <Button variant="danger" onClick={handleCloseError}>
          {localizationStrings?.close || "Close"}
        </Button>
      </Modal>
    </div>
  );
}

export default RadioConfig;
